import { useState } from 'react';

/* useSessionStorage
  Provides a hook-styled way to use the browser's SessionStorage API
  to store/retrieve data from the client's browser.

  It basically just provides a [state, setState] tuple in the same
  format as React's useState hook to allow for similar usage. All
  data stored/retrieved is automatically parsed and stored as a string
  that sessionStorage requires, and returned as a JSON Object.
*/

const useSessionStorage = <T>(key: string, initialValue?: T): [T, (value: T | ((value: T) => T)) => T, () => void, () => T] => {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.sessionStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.warn(error);
      return initialValue;
    }
  });
  const getValue = () => {
    try {
      const item = window.sessionStorage.getItem(key);
      setStoredValue(item ? JSON.parse(item) : initialValue);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.warn(error);
      return setStoredValue(initialValue);
    }
  };
  const setValue = (value) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      window.sessionStorage.setItem(key, JSON.stringify(valueToStore));
      return valueToStore;
    } catch (error) {
      console.error(error);
      return storedValue;
    }
  };
  const deleteValue = () => {
    setStoredValue(undefined);
    window.sessionStorage.removeItem(key);
  };

  return [storedValue, setValue, deleteValue, getValue];
};

const getSessionStorage = <T>(key: string, defaultValue: T): T => {
  try {
    const value = window.sessionStorage.getItem(key);
    return value ? JSON.parse(value) : defaultValue;
  } catch (error) {
    console.warn(error);
    return defaultValue;
  }
};
// setSessionStorage
const setSessionStorage = <T>(key: string, value: T): T => {
  const storedValue = getSessionStorage(key, undefined);
  try {
    const valueToStore = value instanceof Function ? value(storedValue) : value;
    window.sessionStorage.setItem(key, JSON.stringify(valueToStore));
    return valueToStore;
  } catch (error) {
    console.error(error);
    return storedValue;
  }
};

const deleteSessionStorage = (key: string): void => {
  window.sessionStorage.removeItem(key);
};

export default useSessionStorage;
export { getSessionStorage, setSessionStorage, deleteSessionStorage };
